import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faGridRound2,
  faHouse,
  faLightbulb,
  faListTimeline,
  faPlusCircle,
  faPuzzle,
  faScrewdriverWrench,
  faTable,
  faWandMagicSparkles,
  faMemo
} from '@fortawesome/pro-light-svg-icons';

import Home from '../screens/Home';
import AiAgents from '../screens/UserB/AiAgents';
import InsightAgent from '../screens/UserB/InsightAgent';

import Integration from '../screens/UserB/Integration';
import DomainStores from '../screens/UserB/DomainStores';
import BusinessGlossary from '../screens/UserB/BusinessGlossary';
import Tools from '../screens/UserB/Tools';
import MonitoringOverview from '../screens/UserB/MonitoringOverview';
import MonitoringLogs from '../screens/UserB/MonitoringLogs';
import AxtriaSmallLogo from '../assets/image/axtria-icon.svg';
import AxtriaLargeLogo from '../assets/image/AxtriaLogoLarge.svg';
import { FerringSmallLogo, FerringLargeLogo, AxtriaHiqLogoWhite } from '../assets/image/FerringLogos';

import {
  AI_AGENTS_PAGE,
  AI_DASHBOARDS_PAGE,
  INSIGHT_AGENT_PAGE,
  ARTIFACTS_PAGE,
  PINBOARD_PAGE,
  CHAT,
  CHAT_HISTORY,
  DOMAIN_STORES,
  BUSSINESS_GLOSSARY,
  HOME,
  INTEGRATION_PAGE,
  KNOWLEDGE_PAGE,
  LOGS_PAGE,
  MONITORING_PAGE,
  TOOLS_PAGE,
  WORKSPACE_OWNER,

} from '../constants/Constantsi18';
import ChatComponent from '../screens/UserB/ChatComponent';
import ArtifactsComponent from '../screens/UserB/ArtifactsComponent';
import PinboardComponent from '../screens/UserB/PinboardComponent';
import { useSelector } from 'react-redux';

interface NavSubItem {
  id: string;
  path: string;
  title: string;
  show: boolean;
  component: React.ReactElement;
  isUnavailableConfig?: { locked?: boolean; redirectTo?: string; title?: string } | null;
}

interface NavItem {
  id: string;
  path?: string;
  title: string;
  show: boolean;
  component?: React.ReactElement;
  navIcon?: React.ReactElement;
  isUnavailableConfig?: { locked?: boolean; redirectTo?: string; title?: string } | null;
  itHasSubmenu?: boolean;
  submenuList?: NavSubItem[];
  subnavId?: string;
}

const getFerringFlags = (preferredProject: any) => {
  const env = process.env.REACT_APP_ENV;
  const isDevEnv = env === 'dev' || env === 'dev1';
  const isQa = env === 'qa';
  const isPreprod = env === 'preprod';
  const isFerring = isDevEnv && Number(preferredProject) === 79;
  const isFerringQa = isQa && Number(preferredProject) === 12;
  const isFerringPreprod = isPreprod && Number(preferredProject) === 2;
  return {
    isFerring,
    isFerringQa,
    isFerringPreprod,
    isFerringActive: isFerring || isFerringPreprod || isFerringQa,
  };
};

// const getIsNavLocked = (isOnboardingEnabled: boolean, isPortfolioIdMissing: boolean, isInOnboardingFlow: boolean) =>
//   isOnboardingEnabled && (isPortfolioIdMissing || isInOnboardingFlow);

// const buildHomeNavItem = (isWorkSpaceOwner: boolean, isOnboardingEnabled: boolean) => ({
//   isUnavailableConfig: isWorkSpaceOwner
//     ? { locked: true }
//     : !isOnboardingEnabled
//       ? { locked: true, redirectTo: '/chat' }
//       : null,
//   component: !isWorkSpaceOwner && isOnboardingEnabled ? <Home /> : <></>,
// });

const buildBrandLogoItem = (isFerringActive: boolean,brandLogoLarge:any,brandLogoSmall:any) => ({
  id: 'home',
  brandLogoSmall: isFerringActive? 
  <div className="axtria-logo-small">
  <img
    src={FerringSmallLogo}
    alt="brand logo"
    className="brand-logo-img"
    style={{height:"32px", width:"32px" }}
  />
</div> : brandLogoSmall?.url ? (
    <div className="axtria-logo-small">
      <img
        src={brandLogoSmall.url}
        alt="brand logo"
        className="brand-logo-img"
        style={{ width: brandLogoSmall.width ?? undefined, height: brandLogoSmall.height ?? undefined }}
      />
    </div>
  ) : (
    <div className="axtria-logo-small"><AxtriaSmallLogo /></div>
  ),
  brandLogoLarge: isFerringActive?
  <img
      src={FerringLargeLogo}
      alt="brand logo"
      className="brand-logo-img"        
      style={{height:"64px" }}
     
    /> :
  brandLogoLarge?.url ? (
    <img
      src={brandLogoLarge.url}
      alt="brand logo"
      className="brand-logo-img"
      style={{ width: brandLogoLarge.width ?? undefined, height: brandLogoLarge.height ?? undefined , marginBottom:"8px"}}
    />
  ) : (
    <AxtriaLargeLogo />
  ),
  isClickable: true,
  path: '/'
});

const getHomeUnavailableConfig = (isWorkSpaceOwner: boolean, isOnboardingEnabled: boolean) => {
  if (isWorkSpaceOwner) return { locked: true };
  if (!isOnboardingEnabled) return { locked: true, redirectTo: '/chat' };
  return null;
};

const getHomeComponent = (isWorkSpaceOwner: boolean, isOnboardingEnabled: boolean) =>
  !isWorkSpaceOwner && isOnboardingEnabled ? <Home /> : <></>;

const getLockedConfig = (
  isOnboardingEnabled: boolean,
  isPortfolioIdMissing: boolean,
  isInOnboardingFlow: boolean
) => (isOnboardingEnabled && (isPortfolioIdMissing || isInOnboardingFlow) ? { locked: true } : null);

const useRoutes = () => {
  const {
      userInfo,
      navigationInfo,
    } = useSelector((state:any) => ({
      userInfo: state.loginResource,
      navigationInfo: state.navigationInfo,
    }));
  const location = useLocation();

  const profileArray = Array.isArray(userInfo?.profileData)
    ? userInfo.profileData
    : [];

  const profile = profileArray.find(
    (item: any) => item.id === userInfo?.preferredProfile
  );

  const isWorkSpaceOwner =
    profile?.name?.trim().toLowerCase() === WORKSPACE_OWNER.toLowerCase();

  const currentPortfolio = userInfo?.userPortfolios?.find(
    (pf: any) => pf.project_id == userInfo?.preferredProject
  );
  const isPortfolioIdMissing = !currentPortfolio?.is_complete;
  const isInOnboardingFlow = location.pathname === '/' && !userInfo?.isHomepageDashboard;

  const currentCompany = userInfo?.company_data?.find(
    (c: any) => c.companyId == userInfo?.preferredCompany
  );
  const currentProject = currentCompany?.projectList?.find(
    (p: any) => p.project_id == userInfo?.preferredProject
  );
  const isOnboardingEnabled = currentProject?.onboarding && currentProject?.onboarding !== false;

  const [activeNavItem, setActiveNavItem] = useState(
    navigationInfo?.active_menu || (!isOnboardingEnabled ? 'newChat' : 'home')
  );

  const getNavIdFromPath = (pathname: string, search?: string) => {
    const pathSegment = pathname.split('/')[1] || 'home';
    if (pathSegment === 'aiAgents' || pathSegment === 'insightAgent' || pathSegment === 'apis' || pathSegment === 'domainStores' || pathSegment === 'businessGlossary' || pathSegment === 'monitoringDashboard' || pathSegment === 'monitoringLogs') {
      return pathSegment;
    }
    if (pathSegment === 'studio') {
      return 'pinboard';
    }
    if (pathSegment === 'chat') {
      // An existing conversation opened via History (or a deep link/refresh into one)
      // carries conversationId — that's "viewing a conversation", so History stays lit.
      // No conversationId means a genuinely blank chat, so the + icon stays lit.
      const hasConversationId = !!new URLSearchParams(search || '').get('conversationId');
      return hasConversationId ? 'History' : 'newChat';
    }
    return pathSegment === '' ? 'home' : pathSegment;
  };

  // const isNavLocked = getIsNavLocked(isOnboardingEnabled, isPortfolioIdMissing, isInOnboardingFlow);
  // const homeNavItem = buildHomeNavItem(isWorkSpaceOwner, isOnboardingEnabled);

  const navListSelected: NavItem[] = [
    {
      id: 'home',
      isUnavailableConfig: getHomeUnavailableConfig(isWorkSpaceOwner, isOnboardingEnabled),
      path: '/',
      title: HOME,
      show: true,
      navIcon: <FontAwesomeIcon icon={faHouse as IconProp} />,
      component: getHomeComponent(isWorkSpaceOwner, isOnboardingEnabled),
    },
    {
      id: 'newChat',
      isUnavailableConfig: getLockedConfig(isOnboardingEnabled, isPortfolioIdMissing, isInOnboardingFlow),
      path: '/chat',
      title: CHAT,
      show: !isWorkSpaceOwner,
      navIcon: <FontAwesomeIcon icon={faPlusCircle as IconProp} />,
      component: <ChatComponent />,
    },
    {
      id: 'History',
      isUnavailableConfig: getLockedConfig(isOnboardingEnabled, isPortfolioIdMissing, isInOnboardingFlow),
      path: '/',
      title: CHAT_HISTORY,
      navIcon: <FontAwesomeIcon icon={faListTimeline as IconProp} />,
      show: !isWorkSpaceOwner,
      component: <></>,
    },
    {
      id: 'artifacts',
      isUnavailableConfig: getLockedConfig(isOnboardingEnabled, isPortfolioIdMissing, isInOnboardingFlow),
      path: '/artifacts',
      title: ARTIFACTS_PAGE,
      navIcon: <FontAwesomeIcon icon={faMemo as IconProp} />,
      show: !isWorkSpaceOwner,
      component: <ArtifactsComponent />,
    },
    {
      id: 'pinboard',
      isUnavailableConfig: getLockedConfig(isOnboardingEnabled, isPortfolioIdMissing, isInOnboardingFlow),
      path: '/studio',
      title: PINBOARD_PAGE,
      navIcon: <FontAwesomeIcon icon={faWandMagicSparkles as IconProp} />,
      show: !isWorkSpaceOwner,
      component: <PinboardComponent />,
    },

    // {
    //   id: 'aiDashboards',
    //   path: '/aiDashboards',
    //   title: AI_DASHBOARDS_PAGE,
    //   isUnavailableConfig: { locked: true, title: AI_DASHBOARDS_PAGE,},
    //   show: !isWorkSpaceOwner,
    //   navIcon: <FontAwesomeIcon icon={faGridRound2 as IconProp} />,
    //   component: <AiDashboards />,
    // },
    
     {
      id: 'apps',
      itHasSubmenu: true,
      show: isWorkSpaceOwner,
      // isUnavailableConfig: { locked: true,},
      navIcon: <FontAwesomeIcon icon={faGridRound2 as IconProp} />,
      submenuList: [
        {
          id: 'aiAgents',
          path: '/aiAgents',
          title: AI_AGENTS_PAGE,
          // isUnavailableConfig: { locked: true,},
          show: true,
          component: <AiAgents />,
        },
        {
          id: 'insightAgent',
          path: '/insightAgent',
          title: INSIGHT_AGENT_PAGE,
          show: true,
          component: <InsightAgent />,
        },
        //TODO: will be used in future
        // {
        //   id: 'aiDashboards',
        //   path: '/aiDashboards',
        //   title: AI_DASHBOARDS_PAGE,
        //   isUnavailableConfig: { locked: true},
        //   show: true,
        //   component: <AiDashboards />,
        // },
        // {
        //   id: 'apis',
        //   path: '/apis',
        //   title: APIS_PAGE,
        //   isUnavailableConfig: { locked: true,},
        //   show: true,
        //   component: <APIs />,
        // },
      ],
      subnavId: 'apps',
      title: 'Apps',
    },
    
    // {
    //   id: 'History',
    //   path: '/',
    //   title: CHAT_HISTORY,
    //   navIcon: <FontAwesomeIcon icon={faListTimeline} />,
    //   show: isWorkSpaceOwner,
    //   component: <Home />,
    // },
    
    //  {
    //   id: 'chat',
    //   path: '/chat',
    //   show: isWorkSpaceOwner,
    //   title: CHAT,
    //   navIcon: <FontAwesomeIcon icon={faPlusCircle} />,
    //   component: <ChatComponent />,
    // },
    
       {
      id: 'monitoring',
      itHasSubmenu: true,
      show: isWorkSpaceOwner,
      navIcon: <FontAwesomeIcon icon={faTable as IconProp} />,
      submenuList: [
        {
          id: 'monitoringDashboard',
          path: '/monitoringDashboard',
          title:   AI_DASHBOARDS_PAGE,
          show: true,
          component: <MonitoringOverview />,
        },
        {
          id: 'monitoringLogs',
          path: '/monitoringLogs',
          title: LOGS_PAGE,
          show: true,
          component: <MonitoringLogs />,
        },
      ],
      subnavId: 'monitoring',
      title: MONITORING_PAGE,
    },
    
      {
      id: 'tools',
      path: '/tools',
      title: TOOLS_PAGE,
      isUnavailableConfig: { locked: true, title: 'Tools',},
      show: isWorkSpaceOwner,
      navIcon: <FontAwesomeIcon icon={faScrewdriverWrench as IconProp} />,
      component: <Tools />,
    },
  
    {
      id: 'knowledge',
      itHasSubmenu: true,
      show: isWorkSpaceOwner,
      navIcon: <FontAwesomeIcon icon={faLightbulb as IconProp} />,
      submenuList: [
        {
          id: 'domainStores',
          path: '/domainStores',
          title: DOMAIN_STORES,
          show: true,
          component: <DomainStores />,
        },
        {
          id: 'businessGlossary',
          path: '/businessGlossary',
          title: BUSSINESS_GLOSSARY,
          show: true,
          component: <BusinessGlossary />,
        },
      ],
      subnavId: 'knowledge',
      title: KNOWLEDGE_PAGE,
    },
    
      {
      id: 'integration',
      path: '/integration',
      title: INTEGRATION_PAGE,
      show: isWorkSpaceOwner,
      navIcon: <FontAwesomeIcon icon={faPuzzle as IconProp} />,
      component: <Integration />,
    },
  ];


  // Update activeNavItem when path changes or Redux navigation state changes
  useEffect(() => {
    const currentPath = location.pathname;
    const urlNavId = getNavIdFromPath(currentPath, location.search);

    // Only use Redux state if it's not 'home' and URL doesn't suggest a different page
    if (navigationInfo?.active_menu && navigationInfo.active_menu !== '' && navigationInfo.active_menu !== 'home') {
      setActiveNavItem(navigationInfo.active_menu);
    } else {
      setActiveNavItem(urlNavId);
    }
    // location.search must stay a dep: opening/leaving a conversation via History or "+"
    // only changes the conversationId query param, not the pathname (both live on /chat).
  }, [location.pathname, location.search, navigationInfo?.active_menu]);

  const brandLogoSmall = userInfo?.brandLogoSmall;
  const brandLogoLarge = userInfo?.brandLogoLarge;

  // Preload and fully decode both logos so the sidebar's remounted <img> on toggle
  // paints instantly instead of decoding a freshly-mounted large image on the main thread.
  const preloadedLogosRef = useRef<HTMLImageElement[]>([]);

  useEffect(() => {
    const url = brandLogoSmall?.url;
    if (!url) return;
    const img = new Image();
    img.src = url;
    img.decode?.().catch(() => {});
    preloadedLogosRef.current.push(img);
  }, [brandLogoSmall?.url]);

  useEffect(() => {
    const url = brandLogoLarge?.url;
    if (!url) return;
    const img = new Image();
    img.src = url;
    img.decode?.().catch(() => {});
    preloadedLogosRef.current.push(img);
  }, [brandLogoLarge?.url]);

  const { isFerring, isFerringQa, isFerringPreprod } = getFerringFlags(userInfo?.preferredProject);
  const isFerringActive = isFerring || isFerringPreprod || isFerringQa;

  const brandLogoItem = buildBrandLogoItem(isFerringActive,brandLogoLarge,brandLogoSmall);

  const poweredBy={
    id:'poweredBy',
    poweredByLogoSmall:<div className="axtria-logo-small"><AxtriaSmallLogo /></div>,
    poweredByLogoLarge:<img style={{width:'115px', marginLeft: '-20px'}} src={AxtriaHiqLogoWhite} />
  }

  return { navListSelected, brandLogoItem, activeNavItem, isFerring, isFerringPreprod, isFerringQa, poweredBy };
};

export default useRoutes;




